<script lang="ts">
  import { navigate } from '../router.svelte';
  import { handle401 } from '../admin';
  import { CURSOR_START, fetchComments, fetchPosts, toAuthor, withNextCursor, type CommentRow, type CommentPage, type PostPage } from '@api';
  import { queueReply as queueReplyRow } from '@api';
  import SkoolComment from '@ui/SkoolComment.svelte';
  import SkoolPost from '@ui/SkoolPost.svelte';
  import ReplyComposer from '@ui/composer/ReplyComposer.svelte';

  // ONE MEMBER'S POSTS AND COMMENTS — the all-time archive of what they wrote here.
  //
  // Keyed on `memberId` and nothing else, the same way `MemberDmThread` is: the member
  // page was the first mount site and the pipeline card drawer is the second, so neither
  // this component nor its two feeds know what a card is. Everything it shows comes from
  // `GET …/posts` and `GET …/comments` filtered to the member.
  //
  // READING A THREAD IS THE CALLER'S JOB. The member page opens the clicked post in its
  // own side-panel and needs the selection highlighted; the card drawer is already a
  // panel and just links out to Skool. So selection is a pair of props in and a pair of
  // callbacks out — with no `onpost`/`oncomment` the cards render inert, which is exactly
  // what a surface with nowhere to open a thread wants.
  let {
    skoolId,
    memberId,
    layout = 'columns',
    pageSize = 10,
    nowMs = Date.now(),
    selectedPostId = null,
    selectedCommentId = null,
    onpost = null,
    oncomment = null,
    canReply = false,
  }: {
    skoolId: number | null;
    memberId: number | null;
    /** `columns` side-by-side (a page); `stacked` one above the other (a drawer). */
    layout?: 'columns' | 'stacked';
    pageSize?: number;
    nowMs?: number;
    selectedPostId?: number | null;
    selectedCommentId?: number | null;
    onpost?: ((postId: number) => void) | null;
    oncomment?: ((comment: CommentRow) => void) | null;
    /**
     * Show each card's "Reply" link, opening one composer under the card it was
     * clicked on.
     *
     * MUTUALLY EXCLUSIVE WITH `onpost`/`oncomment`, and not by this component's choice:
     * `SkoolPost`/`SkoolComment` gate Reply on `!onclick`, because in summary mode the
     * whole card is one stretched click target and a link inside it would be
     * unclickable. So a surface that opens threads (the member page) replies IN the
     * thread it opens, and one with nowhere to open (the pipeline card drawer) replies
     * here.
     */
    canReply?: boolean;
  } = $props();

  /**
   * Queue a reply to one of these posts/comments for the Helper to send — the same
   * `POST …/outbox/items` upsert the moderation inbox writes, so a reply queued here
   * lands in the one queue the Outbox surface already reviews and sends.
   *
   * Rethrows so `ReplyComposer` can render the server's own sentence: the refusals here
   * are ones a person has to read, not retry through (`409 reply_in_flight` — it may
   * already be on Skool; `400 reply_unaddressable`). Only 401 is swallowed, by the
   * navigate that handles it.
   */
  async function queueReply(kind: 'post' | 'comment', id: number, body: string): Promise<void> {
    if (skoolId == null) return;
    try {
      await queueReplyRow(skoolId, kind, id, body);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      throw err;
    }
  }

  /** The one card whose reply box is open. One at a time, deliberately: two half-written
   *  replies on screen is a way to send the wrong one to the wrong person. */
  let replyTo = $state<{ kind: 'post' | 'comment'; id: number } | null>(null);
  const isReplying = (kind: 'post' | 'comment', id: number): boolean =>
    replyTo?.kind === kind && replyTo.id === id;

  // Both feeds are CURSOR-paginated in v1 (§1.3 licenses offset on three routes and these
  // are not among them), so each keeps the cursors it has already been handed: entry `i`
  // fetches page `i`, and going back is a lookup rather than a new kind of request.
  // `total` still comes back, so the "1–10 of 137" readout survives.
  let posts = $state<PostPage | null>(null);
  let postCursors = $state<(string | null)[]>(CURSOR_START);
  let postPage = $state(0);
  let comments = $state<CommentPage | null>(null);
  let commentCursors = $state<(string | null)[]>(CURSOR_START);
  let commentPage = $state(0);

  function adoptPosts(page: PostPage, index = 0): void {
    posts = page;
    postPage = index;
    postCursors = withNextCursor(postCursors, index, page.next_cursor);
  }
  function adoptComments(page: CommentPage, index = 0): void {
    comments = page;
    commentPage = index;
    commentCursors = withNextCursor(commentCursors, index, page.next_cursor);
  }

  // community-effect-ok: keys on `memberId` as well, and must CLEAR the previous
  // member's pages — `skoolId` arrives as a prop and is read unconditionally, so the
  // dependency this check exists to protect cannot go untracked here.
  $effect(() => {
    const sid = skoolId;
    const mid = memberId;
    const limit = pageSize;
    let alive = true;
    posts = null;
    comments = null;
    postCursors = CURSOR_START;
    commentCursors = CURSOR_START;
    postPage = 0;
    commentPage = 0;
    if (sid == null || mid == null) return;
    void fetchPosts(sid, { memberId: mid, limit })
      .then((page) => {
        if (alive) adoptPosts(page);
      })
      .catch(() => {
        /* an empty feed and a failed one read the same here — the page owns errors */
      });
    void fetchComments(sid, { memberId: mid, limit, withPost: true })
      .then((page) => {
        if (alive) adoptComments(page);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  });

  async function loadPostsPage(index: number): Promise<void> {
    if (skoolId == null || memberId == null) return;
    if (index < 0 || index >= postCursors.length) return;
    const page = await fetchPosts(skoolId, {
      memberId,
      limit: pageSize,
      cursor: postCursors[index] ?? null,
    });
    adoptPosts(page, index);
  }

  async function loadCommentsPage(index: number): Promise<void> {
    if (skoolId == null || memberId == null) return;
    if (index < 0 || index >= commentCursors.length) return;
    const page = await fetchComments(skoolId, {
      memberId,
      limit: pageSize,
      withPost: true,
      cursor: commentCursors[index] ?? null,
    });
    adoptComments(page, index);
  }
</script>

<div class="grid gap-5 {layout === 'columns' ? 'lg:grid-cols-2' : ''}">
  <!-- Posts -->
  <section class="min-w-0">
    <div class="eyebrow mb-3">Posts{#if posts?.total != null} · {posts.total.toLocaleString()}{/if}</div>
    {#if !posts || posts.items.length === 0}
      <p class="text-sm text-base-content/62">No posts.</p>
    {:else}
      <div class="flex flex-col gap-3">
        {#each posts.items as p (p.id)}
          <!-- privacy-ok: handed to <SkoolPost>, which marks the body it renders -->
          <SkoolPost
            author={toAuthor(p.author)}
            title={p.title}
            body={p.excerpt ?? null}
            images={p.images ?? null}
            videos={p.videos ?? null}
            mediaSide
            skoolUrl={p.url}
            upvotes={p.upvotes}
            commentCount={p.comment_count}
            createdAt={p.created_at}
            pinned={p.pinned}
            selected={selectedCommentId == null && selectedPostId === p.id}
            now={nowMs}
            onclick={onpost ? () => onpost(p.id) : null}
            onReply={canReply ? () => (replyTo = { kind: 'post', id: p.id }) : null}
            replyActive={isReplying('post', p.id)}
          />
          {#if isReplying('post', p.id)}
            <ReplyComposer
              {skoolId}
              kind="post"
              targetId={p.id}
              submitLabel="Reply"
              doneLabel="Queued"
              onSubmit={(text) => queueReply('post', p.id, text)}
            />
          {/if}
        {/each}
      </div>
      {#if postCursors.length > 1 || postPage > 0}
        {@const from = postPage * pageSize}
        <div class="mt-3 flex items-center justify-between font-mono text-xs text-base-content/72">
          <button class="btn btn-ghost btn-xs" disabled={postPage === 0} onclick={() => loadPostsPage(postPage - 1)}>← Prev</button>
          <span>{from + 1}–{from + posts.items.length}{#if posts.total != null} of {posts.total.toLocaleString()}{/if}</span>
          <button class="btn btn-ghost btn-xs" disabled={postPage + 1 >= postCursors.length} onclick={() => loadPostsPage(postPage + 1)}>Next →</button>
        </div>
      {/if}
    {/if}
  </section>

  <!-- Comments -->
  <section class="min-w-0">
    <div class="eyebrow mb-3">Comments{#if comments?.total != null} · {comments.total.toLocaleString()}{/if}</div>
    {#if !comments || comments.items.length === 0}
      <p class="text-sm text-base-content/62">No comments.</p>
    {:else}
      <div class="flex flex-col gap-3">
        {#each comments.items as c (c.id)}
          <SkoolComment
            author={toAuthor(c.author)}
            body={c.content}
            images={c.images}
            videos={c.videos}
            mediaSide
            skoolUrl={c.post?.url ?? c.url}
            upvotes={c.upvotes}
            createdAt={c.created_at}
            pinned={c.pinned}
            selected={selectedCommentId === c.id}
            now={nowMs}
            replyCount={c.reply_count ?? null}
            onclick={oncomment ? () => oncomment(c) : null}
            boxed
            onReply={canReply ? () => (replyTo = { kind: 'comment', id: c.id }) : null}
            replyActive={isReplying('comment', c.id)}
          >
            {#snippet badges()}
              {#if c.post?.title}
                <span class="redact min-w-0 max-w-full truncate text-xs text-base-content/72">on {c.post.title}</span>
              {/if}
            {/snippet}
          </SkoolComment>
          {#if isReplying('comment', c.id)}
            <ReplyComposer
              {skoolId}
              kind="comment"
              targetId={c.id}
              submitLabel="Reply"
              doneLabel="Queued"
              onSubmit={(text) => queueReply('comment', c.id, text)}
            />
          {/if}
        {/each}
      </div>
      {#if commentCursors.length > 1 || commentPage > 0}
        {@const from = commentPage * pageSize}
        <div class="mt-3 flex items-center justify-between font-mono text-xs text-base-content/72">
          <button class="btn btn-ghost btn-xs" disabled={commentPage === 0} onclick={() => loadCommentsPage(commentPage - 1)}>← Prev</button>
          <span>{from + 1}–{from + comments.items.length}{#if comments.total != null} of {comments.total.toLocaleString()}{/if}</span>
          <button class="btn btn-ghost btn-xs" disabled={commentPage + 1 >= commentCursors.length} onclick={() => loadCommentsPage(commentPage + 1)}>Next →</button>
        </div>
      {/if}
    {/if}
  </section>
</div>
